import React, { useState } from 'react';
import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';

const PressReleaseActions = ({ formData, onValidate }) => {
  const [copySuccess, setCopySuccess] = useState(false);
  const [downloadLoading, setDownloadLoading] = useState(false);

  const generatePressReleaseText = () => {
    const currentDate = new Date()?.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });

    return `The Surgical Detail Most Patients Never Hear About—Until Now

FOR IMMEDIATE RELEASE

${currentDate}

${formData?.city}, ${formData?.state}

${formData?.practiceName}, led by Dr. ${formData?.surgeonName}, is now offering VeraForm®, an FDA-cleared three-dimensional tissue marker developed by Videra Surgical, as an option for patients undergoing breast cancer surgery.

While most patients focus on the tumor removal itself, surgeons must also consider how the surgical site will be identified on future imaging and during post-operative planning. VeraForm® is designed to provide a continuous, three-dimensional, radiopaque marker that identifies the surgical site after the procedure is complete.

"${formData?.quote}"

VeraForm® is placed at the time of surgery and is visible across standard imaging modalities. Unlike traditional point markers or clips, the device is designed to create a continuous reference of the surgical site, which may assist care teams during post-surgical imaging review and radiation planning, when applicable.

Dr. ${formData?.surgeonName} and the team at ${formData?.practiceName} will discuss whether VeraForm® is appropriate during individualized patient consultations, based on surgical approach and overall treatment planning.

________________

About VeraForm®

VeraForm® is an FDA 510(k)-cleared tissue marker developed by Videra Surgical. The device is designed to provide a permanent, continuous, three-dimensional radiopaque mark of a surgical site to support post-operative imaging and planning. VeraForm® is intended to complement surgical technique and clinical judgment.

About ${formData?.practiceName}

${formData?.practiceName} is a ${formData?.practiceDescription || '[brief, standard practice description if desired]'} committed to evidence-based care, patient education, and thoughtful adoption of surgical technologies.

________________

Media Contact

${formData?.contactName}
${formData?.contactTitle || '[TITLE]'}
${formData?.contactPhone}
${formData?.contactEmail}
${formData?.website || '[WEBSITE]'}`;
  };

  const handleCopyToClipboard = async () => {
    if (!onValidate()) {
      return;
    }

    try {
      const pressReleaseText = generatePressReleaseText();
      await navigator.clipboard?.writeText(pressReleaseText);
      setCopySuccess(true);
      setTimeout(() => setCopySuccess(false), 3000);
    } catch (error) {
      console.error('Failed to copy to clipboard:', error);
      alert('Failed to copy to clipboard. Please try again.');
    }
  };

  const handleDownloadPDF = async () => {
    if (!onValidate()) {
      return;
    }

    setDownloadLoading(true);

    try {
      const pressReleaseText = generatePressReleaseText();
      const blob = new Blob([pressReleaseText], { type: 'text/plain' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `press-release-${formData?.practiceName?.replace(/\s+/g, '-')?.toLowerCase()}-${Date.now()}.txt`;
      document.body?.appendChild(link);
      link?.click();
      document.body?.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Download failed:', error);
      alert('Download failed. Please try again.');
    } finally {
      setDownloadLoading(false);
    }
  };

  const handleDownloadDOCX = async () => {
    if (!onValidate()) {
      return;
    }

    setDownloadLoading(true);

    try {
      const pressReleaseText = generatePressReleaseText();
      const blob = new Blob([pressReleaseText], { type: 'application/msword' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `press-release-${formData?.practiceName?.replace(/\s+/g, '-')?.toLowerCase()}-${Date.now()}.doc`;
      document.body?.appendChild(link);
      link?.click();
      document.body?.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Download failed:', error);
      alert('Download failed. Please try again.');
    } finally {
      setDownloadLoading(false);
    }
  };

  return (
    <div className="bg-card border border-border rounded-lg p-6 md:p-8 shadow-sm">
      <div className="flex items-center gap-3 mb-6">
        <div className="w-10 h-10 bg-success/10 rounded-lg flex items-center justify-center">
          <Icon name="Download" size={20} color="var(--color-success)" />
        </div>
        <div>
          <h2 className="text-xl md:text-2xl font-semibold text-foreground">Export Options</h2>
          <p className="text-sm text-muted-foreground font-caption">Download or copy your press release</p>
        </div>
      </div>

      <div className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Button
            variant="default"
            onClick={handleDownloadDOCX}
            loading={downloadLoading}
            iconName="FileText"
            iconPosition="left"
            fullWidth
          >
            Download DOCX
          </Button>

          <Button
            variant="outline"
            onClick={handleDownloadPDF}
            loading={downloadLoading}
            iconName="FileDown"
            iconPosition="left"
            fullWidth
          >
            Download TXT
          </Button>
        </div>

        <Button
          variant={copySuccess ? 'success' : 'secondary'}
          onClick={handleCopyToClipboard}
          iconName={copySuccess ? 'Check' : 'Copy'}
          iconPosition="left"
          fullWidth
        >
          {copySuccess ? 'Copied to Clipboard!' : 'Copy to Clipboard'}
        </Button>

        <div className="bg-muted/30 border border-border rounded-lg p-4 mt-6">
          <div className="flex items-start gap-3">
            <Icon name="Info" size={20} className="text-primary mt-0.5 flex-shrink-0" />
            <div className="space-y-2">
              <p className="text-sm font-medium text-foreground">Distribution Guidelines</p>
              <ul className="text-sm text-muted-foreground space-y-1 list-disc list-inside font-caption">
                <li>Review all information for accuracy before distribution</li>
                <li>Ensure compliance with practice marketing policies</li>
                <li>Coordinate with Videra Surgical for co-marketing opportunities</li>
                <li>Submit to local and industry-specific media outlets</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PressReleaseActions;